import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useContent } from "../contexts/ContentContext";
import { Copy, CheckCircle2, Loader2, ShieldAlert, ChevronRight, IdCard, UserCircle2 } from "lucide-react";
import { toast } from "sonner";
import LivePhotoCapture from "../components/LivePhotoCapture";

const STEPS = [
  { id: 1, label: "Vos informations" },
  { id: 2, label: "Pièce d'identité" },
  { id: 3, label: "Selfie en direct" },
  { id: 4, label: "Code d'accès" },
];

export default function Register() {
  const { login } = useAuth();
  const { t } = useContent();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ name: "", email: "", phone: "", role: "client" });
  const [idDocFileId, setIdDocFileId] = useState("");
  const [selfieFileId, setSelfieFileId] = useState("");
  const [idDocType, setIdDocType] = useState("cni");
  const [accessCode, setAccessCode] = useState("");
  const [token, setToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submitStep1 = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setAccessCode(data.access_code);
      setToken(data.token);
      // Log the user in to enable KYC upload
      login(data.token, data.user);
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitKyc = async () => {
    if (!idDocFileId || !selfieFileId) {
      toast.error("Capturez votre pièce d'identité et votre selfie pour continuer.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/kyc", {
        id_document_file_id: idDocFileId,
        selfie_file_id: selfieFileId,
        id_document_type: idDocType,
      });
      setStep(4);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const copy = () => {
    navigator.clipboard.writeText(accessCode);
    setCopied(true);
    toast.success("Code copié !");
    setTimeout(() => setCopied(false), 2000);
  };

  const finish = () => {
    if (form.role === "artist") navigate("/artist/onboarding");
    else navigate("/explore");
  };

  return (
    <div className="container-mc py-10 md:py-16" data-testid="register-page">
      <div className="max-w-2xl mx-auto">
        {/* Stepper */}
        <ol className="grid grid-cols-4 gap-2 mb-8" data-testid="register-stepper">
          {STEPS.map((s) => {
            const done = step > s.id;
            const cur = step === s.id;
            return (
              <li key={s.id} className="flex flex-col gap-1.5">
                <div className={`h-1 ${done ? "bg-[hsl(var(--accent))]" : cur ? "bg-foreground" : "bg-border"}`} />
                <p className={`text-[10px] font-mono uppercase tracking-wider ${cur ? "text-foreground" : "text-muted-foreground"}`}>
                  {String(s.id).padStart(2, "0")} · {s.label}
                </p>
              </li>
            );
          })}
        </ol>

        {/* STEP 1 */}
        {step === 1 && (
          <form onSubmit={submitStep1} className="card-mc p-7 md:p-8">
            <p className="overline mb-2">Étape 1/4 · Créer un compte</p>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-6">Rejoindre <span className="text-[hsl(var(--primary))]">MCZ EVENTS</span></h1>

            <div className="grid grid-cols-2 gap-3 mb-6">
              {[
                { id: "client", label: "Client", desc: "Je cherche un artiste" },
                { id: "artist", label: "Artiste", desc: "Je veux être sollicité" },
              ].map((r) => (
                <button key={r.id} type="button" data-testid={`role-${r.id}`}
                  onClick={() => set("role", r.id)}
                  className={`p-4 text-left border rounded-sm transition-all ${form.role === r.id ? "border-foreground bg-foreground text-background -translate-y-0.5" : "border-border hover:border-foreground"}`}>
                  <p className="font-display font-black text-lg">{r.label}</p>
                  <p className={`text-xs ${form.role === r.id ? "text-black/60" : "text-muted-foreground"}`}>{r.desc}</p>
                </button>
              ))}
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Nom complet</label>
                <input data-testid="reg-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1 w-full border border-border px-3 py-2.5 rounded-sm bg-card" placeholder="Aïssatou Diallo" />
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Email</label>
                <input data-testid="reg-email" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1 w-full border border-border px-3 py-2.5 rounded-sm bg-card" placeholder="vous@email.com" />
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Téléphone (Guinée)</label>
                <input data-testid="reg-phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} className="mt-1 w-full border border-border px-3 py-2.5 rounded-sm bg-card font-mono" placeholder="+224 622 00 00 00" />
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-primary w-full mt-6" data-testid="reg-submit">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Continuer <ChevronRight className="w-4 h-4 ml-1" />
            </button>

            <p className="text-xs text-muted-foreground mt-6 text-center">
              Déjà inscrit ? <Link to="/login" className="font-bold underline">Connexion</Link>
            </p>
          </form>
        )}

        {/* STEP 2 - ID DOCUMENT */}
        {step === 2 && (
          <div className="card-mc p-7 md:p-8" data-testid="register-step-id">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-[hsl(var(--primary))] flex items-center justify-center rounded-xl">
                <IdCard className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="overline">Étape 2/4 · Vérification d'identité</p>
                <h2 className="font-display font-black text-2xl tracking-tighter">Pièce d'identité</h2>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              {t("register.kyc_id_help", "Pour la sécurité de tous, prenez une photo en direct de votre pièce d'identité avec votre caméra. Aucun téléversement de fichier n'est accepté.")}
            </p>

            <div className="mb-4">
              <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Type de pièce</label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                {[
                  { id: "cni", label: "CNI" },
                  { id: "passport", label: "Passeport" },
                  { id: "permis", label: "Permis" },
                ].map((d) => (
                  <button key={d.id} type="button" data-testid={`doctype-${d.id}`}
                    onClick={() => setIdDocType(d.id)}
                    className={`p-2.5 border rounded-sm text-sm font-bold transition-colors ${idDocType === d.id ? "border-foreground bg-foreground text-background" : "border-border hover:border-foreground"}`}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            <LivePhotoCapture
              testid="kyc-id-capture"
              hint="Placez la pièce dans le cadre, bien éclairée, et capturez."
              facingMode="environment"
              aspect="aspect-[4/3]"
              value={idDocFileId}
              onCapture={setIdDocFileId}
            />

            <div className="mt-6 flex gap-3">
              <button onClick={() => setStep(1)} className="btn-outline" data-testid="step2-back">Retour</button>
              <button onClick={() => setStep(3)} disabled={!idDocFileId} className="btn-primary flex-1" data-testid="step2-next">
                Continuer <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 - SELFIE */}
        {step === 3 && (
          <div className="card-mc p-7 md:p-8" data-testid="register-step-selfie">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-[hsl(var(--primary))] flex items-center justify-center rounded-xl">
                <UserCircle2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="overline">Étape 3/4 · Selfie de vérification</p>
                <h2 className="font-display font-black text-2xl tracking-tighter">Votre visage</h2>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              {t("register.kyc_selfie_help", "Regardez votre caméra et capturez un selfie en direct. Cette photo confirme que c'est bien vous qui créez le compte.")}
            </p>

            <LivePhotoCapture
              testid="kyc-selfie-capture"
              hint="Visage centré, bonne lumière, sans lunettes de soleil."
              facingMode="user"
              aspect="aspect-square"
              value={selfieFileId}
              onCapture={setSelfieFileId}
            />

            <div className="mt-6 flex gap-3">
              <button onClick={() => setStep(2)} className="btn-outline" data-testid="step3-back">Retour</button>
              <button onClick={submitKyc} disabled={!selfieFileId || loading} className="btn-primary flex-1" data-testid="step3-submit">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Valider mon identité
              </button>
            </div>
          </div>
        )}

        {/* STEP 4 - ACCESS CODE */}
        {step === 4 && (
          <div className="card-mc p-7 md:p-8" data-testid="register-success">
            <CheckCircle2 className="w-12 h-12 text-[hsl(var(--accent))] mb-4" />
            <p className="overline mb-2">Étape 4/4 · Compte vérifié</p>
            <h1 className="font-display font-black text-3xl md:text-4xl tracking-tighter mb-2">Votre code d'accès</h1>
            <p className="text-muted-foreground mb-6 text-sm">
              Votre identité a été validée. Conservez précieusement votre code — il vous sera demandé à chaque connexion et ne sera plus jamais affiché.
            </p>

            <div className="card-mc bg-foreground text-background p-6 text-center" data-testid="access-code-display">
              <p className="text-[10px] font-mono text-white/60 tracking-[0.3em] uppercase mb-3">Votre code unique</p>
              <p className="font-mono text-3xl md:text-4xl font-black tracking-[0.4em]">{accessCode}</p>
            </div>

            <button onClick={copy} className="btn-outline w-full mt-4" data-testid="copy-code-btn">
              {copied ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? "Copié !" : "Copier le code"}
            </button>

            <div className="mt-6 p-4 border border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 flex gap-3">
              <ShieldAlert className="w-5 h-5 text-[hsl(var(--primary))] flex-shrink-0" />
              <p className="text-xs leading-relaxed">
                <strong>Important :</strong> sauvegardez ce code immédiatement. En cas d'oubli, vous devrez recréer un compte (avec une nouvelle vérification d'identité).
              </p>
            </div>

            <button onClick={finish} className="btn-primary w-full mt-6" data-testid="continue-after-register">
              {form.role === "artist" ? "Compléter mon profil artiste →" : "Explorer les artistes →"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
